import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Camera, 
  Package, 
  Upload,
  ArrowRight,
  Loader2,
  Info,
  Link as LinkIcon,
  Image as ImageIcon,
  X
} from "lucide-react";

const AddProductPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    condition: "",
    image_url: ""
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageTab, setImageTab] = useState("upload");

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setFormData(prev => ({ ...prev, image_url: url }));
    if (url) {
      setImagePreview(url);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Tipo de archivo no permitido. Use JPEG, PNG, WebP o GIF.");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("El archivo es demasiado grande. Máximo 5MB.");
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      const response = await axios.post(`${API}/upload-image`, formDataUpload, {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      const imageUrl = response.data.image_url;
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      setImagePreview(imageUrl);
      toast.success("Imagen subida correctamente");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Error al subir la imagen");
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => {
    setFormData(prev => ({ ...prev, image_url: "" }));
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.image_url) {
      toast.error("Por favor, añade una imagen del producto");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/products`, formData, { withCredentials: true });
      toast.success("¡Producto añadido! Los administradores han sido notificados por email.");
      navigate("/perfil");
    } catch (error) {
      const message = error.response?.data?.detail || "Error al añadir el producto";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const conditions = [
    { value: "nuevo", label: "Nuevo - Sin usar" },
    { value: "bueno", label: "Buen estado - Usado pero en perfectas condiciones" },
    { value: "aceptable", label: "Aceptable - Con algún desperfecto menor" }
  ];

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#374B67] mb-2" data-testid="add-product-title">
            Añadir Producto
          </h1>
          <p className="text-slate-600">
            Sube un producto para donar y gana 100 puntos cuando sea validado
          </p>
        </div>

        {/* Info Card */}
        <Card className="bg-[#69C7EA]/10 border-[#69C7EA]/30 mb-8">
          <CardContent className="p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-[#374B67] mt-0.5 flex-shrink-0" />
            <div className="text-sm text-[#374B67]">
              <p className="font-semibold mb-1">¿Cómo funciona?</p>
              <ol className="list-decimal list-inside space-y-1 text-slate-600">
                <li>Rellena el formulario con los datos del producto</li>
                <li>Los técnicos recibirán un email y validarán tu producto</li>
                <li>Cuando lo aprueban, recibes <span className="font-semibold text-green-600">+100 puntos</span></li>
                <li>Lleva el producto al Punto Limpio</li>
              </ol>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#374B67]">
              <Package className="w-5 h-5" />
              Datos del Producto
            </CardTitle>
            <CardDescription>
              Proporciona información detallada para facilitar la validación
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Image Upload */}
              <div className="space-y-2">
                <Label>Imagen del producto</Label>
                
                <Tabs value={imageTab} onValueChange={setImageTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="upload" className="flex items-center gap-2">
                      <Upload className="w-4 h-4" />
                      Subir foto
                    </TabsTrigger>
                    <TabsTrigger value="url" className="flex items-center gap-2">
                      <LinkIcon className="w-4 h-4" />
                      URL externa
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="upload" className="mt-4">
                    {imagePreview ? (
                      <div className="relative">
                        <div className="aspect-video rounded-lg overflow-hidden bg-slate-100 border-2 border-dashed border-slate-200">
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2 rounded-full"
                          onClick={clearImage}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div 
                        className="aspect-video rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center cursor-pointer hover:border-[#374B67] hover:bg-slate-100 transition-colors"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="w-10 h-10 text-[#374B67] animate-spin mb-2" />
                            <p className="text-slate-600">Subiendo imagen...</p>
                          </>
                        ) : (
                          <>
                            <Camera className="w-10 h-10 text-slate-400 mb-2" />
                            <p className="text-slate-600 font-medium">Haz clic para subir una foto</p>
                            <p className="text-xs text-slate-400 mt-1">JPEG, PNG, WebP o GIF (máx. 5MB)</p>
                          </>
                        )}
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="file-input"
                    />
                  </TabsContent>
                  
                  <TabsContent value="url" className="mt-4">
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <Input
                          type="url"
                          placeholder="https://ejemplo.com/imagen.jpg"
                          value={imageTab === "url" ? formData.image_url : ""}
                          onChange={handleImageUrlChange}
                          data-testid="image-url-input"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Puedes usar servicios como Imgur para subir tu imagen
                        </p>
                      </div>
                      {imageTab === "url" && imagePreview && (
                        <div className="w-20 h-20 rounded-lg overflow-hidden border border-slate-200">
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            className="w-full h-full object-cover"
                            onError={() => setImagePreview(null)}
                          />
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </div>

              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Nombre del producto</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Ej: Sofá de tres plazas"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="product-name-input"
                />
              </div>

              {/* Category & Condition */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Categoría</Label>
                  <Select value={formData.category} onValueChange={(v) => handleSelectChange("category", v)} required>
                    <SelectTrigger data-testid="category-select">
                      <SelectValue placeholder="Seleccionar categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Estado del producto</Label>
                  <Select value={formData.condition} onValueChange={(v) => handleSelectChange("condition", v)} required>
                    <SelectTrigger data-testid="condition-select">
                      <SelectValue placeholder="Seleccionar estado" />
                    </SelectTrigger>
                    <SelectContent>
                      {conditions.map((cond) => (
                        <SelectItem key={cond.value} value={cond.value}>{cond.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder="Describe el producto: medidas, color, estado de funcionamiento, etc."
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  required
                  data-testid="description-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-[#374B67] hover:bg-[#2C3E56] text-base rounded-full"
                disabled={loading || !formData.image_url}
                data-testid="submit-product-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    Enviar para Validación
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AddProductPage;
